<template>
  <ModalBase
    :show-modal="true"
    @close-modal="close"
    :auto-h="true"
    :width-ms="true"
    :overlay-scrollb="false"
    class="add-supplier-modal"
  >
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal title-modal--simple">
        <template v-slot:title>
          <span>{{ $gettext('Batch Upload') }}</span>
        </template>
        <template v-slot:subtitle>
          <span class="subtitle-text">{{
            $gettext('You can upload multiple costs at once by attaching your PDF or image files.')
          }}</span>
        </template>
      </TitleBar>
    </template>

    <template v-slot:maincontent>
      <div ref="massUploadContainer" class="mass-upload-container">
        <transition
          name="mass-upload-step"
          mode="out-in"
          @before-enter="beforeStepEnter"
          @before-leave="beforeStepLeave"
          @enter="onStepEnter"
          @after-leave="afterStepLeave"
          @after-enter="afterStepEnter"
        >
          <div v-if="!isUploading" key="upload-form" class="mass-upload-step-panel">
            <div class="mass-upload-layout">
              <div class="mass-upload-panel-left" :class="{ 'full-width': !uploadedFiles.length }">
                <div
                  class="dropzone"
                  :class="{ 'dropzone--active': isDragActive }"
                  @click="openFilePicker"
                  @dragenter.prevent="setDragActive(true)"
                  @dragover.prevent="setDragActive(true)"
                  @dragleave.prevent="handleDragLeave"
                  @drop.prevent="handleDrop"
                >
                  <div class="dropzone-content">
                    <font-awesome-icon icon="fa-solid fa-upload" class="dropzone-hint-icon" />
                    <div class="dropzone-hint-text">{{ $gettext('Click or drag your files here') }}</div>
                    <div v-if="!uploadedFiles.length" class="dropzone-limits">
                      {{ $gettext('Upload up to 25 files at once (max 5 MB each)') }}
                    </div>
                  </div>
                </div>
              </div>

              <input
                ref="fileInput"
                type="file"
                class="d-none"
                multiple
                accept=".pdf,image/*"
                @change="handleFileSelection"
              />

              <div v-if="uploadedFiles.length" class="mass-upload-panel-right">
                <div class="uploaded-files-card">
                  <div class="uploaded-files-header">
                    <strong>{{ $gettext('Selected files') }}</strong>
                    <span class="badge uploaded-files-badge">{{ uploadedFiles.length }}/25</span>
                  </div>

                  <overlay-scrollbars
                    class="uploaded-files-list"
                    :options="{ scrollbars: { autoHide: 'leave' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }"
                  >
                    <ul class="queued-file-list">
                      <li
                        v-for="(file, index) in uploadedFiles"
                        :key="`${file.name}-${file.size}-${index}`"
                        class="queued-file-item"
                      >
                        <div class="queued-file-info">
                          <div class="queued-file-icon">
                            <i class="fa-regular fa-file-lines"></i>
                          </div>
                          <div class="queued-file-details">
                            <div class="queued-file-name">{{ file.name }}</div>
                            <div class="queued-file-size">{{ formatFileSize(file.size) }}</div>
                          </div>
                        </div>
                        <GekkoButton
                          class="queued-file-remove-btn"
                          variant="link"
                          icon="fa-solid fa-trash-can"
                          size="sm"
                          @click.stop="removeFile(index)"
                        />
                      </li>
                    </ul>
                  </overlay-scrollbars>
                </div>
              </div>
            </div>

            <div v-if="uploadError" class="alert alert-danger upload-error-alert" role="alert">
              {{ uploadError }}
            </div>
          </div>

          <div v-else key="upload-progress" class="mass-upload-step-panel">
            <div class="upload-progress">
              <div class="upload-progress__body">
                <div :class="['upload-progress__bar', { 'upload-progress__bar--active': isUploading }]">
                  <b-progress :value="uploadProgressPercentage" :max="100" variant="primary"></b-progress>
                </div>

                <div
                  v-if="!isUploadingComplete"
                  class="upload-progress__stats"
                >
                  <span>{{ $gettext("Uploading files... Please don't close the window.") }}</span>
                  <span>{{ completedUploads }}/{{ totalUploads }}</span>
                </div>

                <div v-if="isUploadingComplete" class="upload-progress__success">
                  <div class="upload-progress__success-animation">
                    <AnimationItem
                      :animation="{ animationData: successAnimation.default, loop: false }"
                      :key="'anim_success'"
                    />
                  </div>
                  <span class="upload-progress__success-text">{{ $gettext('Upload complete!') }}</span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </template>

    <template v-slot:actions>
      <GekkoButton
        @click="upload()"
        icon="fa-solid fa-upload"
        :text="$gettext('Upload')"
        :disabled="isUploading || !uploadedFiles.length"
        variant="primary"
        class="pull-right"
      />
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import AiAssistantService from '@/store/services/ai.assistant.service'
import * as successAnimation from '@/css/animations/success-check.json'
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'

export default {
  name: 'MassUploadCostsModal',
  components: {
    ModalBase,
    TitleBar,
    GekkoButton,
    AnimationItem,
  },
  filters: {
    formatCount(value) {
      return value
    },
  },
  data() {
    return {
      isUploading: false,
      isUploadingComplete: false,
      isDragActive: false,
      uploadError: '',
      uploadedFiles: [],
      completedUploads: 0,
      totalUploads: 0,
      maxFiles: 25,
      maxFileSizeBytes: 5 * 1024 * 1024,
      successAnimation: successAnimation,
      newSuppliers: [],
      stepResizeObserver: null,
      resizeRafId: null,
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
    }),
    uploadProgressPercentage() {
      if (!this.totalUploads) {
        return 0
      }

      const percentage = Math.round((this.completedUploads / this.totalUploads) * 100)

      if (percentage <= 0) {
        return 100 / this.maxFiles / 2 // Show a small progress for the first file to indicate that the upload has started
      }

      return percentage
    },
  },
  mounted() {
    this.$nextTick(() => {
      const panel = this.$el.querySelector('.mass-upload-step-panel')
      if (panel) {
        this.observeStepPanel(panel)
        this.setStepContainerHeight(panel.offsetHeight)
      }
    })
  },
  beforeDestroy() {
    this.disconnectStepObserver()
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId)
      this.resizeRafId = null
    }
  },
  methods: {
    setStepContainerHeight(height) {
      const container = this.$refs.massUploadContainer
      if (!container || height == null) return
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId)
      }
      this.resizeRafId = requestAnimationFrame(() => {
        container.style.height = `${Math.ceil(height)}px`
      })
    },
    disconnectStepObserver() {
      if (this.stepResizeObserver) {
        this.stepResizeObserver.disconnect()
        this.stepResizeObserver = null
      }
    },
    observeStepPanel(panelEl) {
      this.disconnectStepObserver()
      if (typeof ResizeObserver === 'undefined' || !panelEl) return
      this.stepResizeObserver = new ResizeObserver((entries) => {
        if (!entries || !entries.length) return
        this.setStepContainerHeight(entries[0].contentRect.height)
      })
      this.stepResizeObserver.observe(panelEl)
    },
    beforeStepEnter() {
      const container = this.$refs.massUploadContainer
      if (!container) return
      if (!container.style.height || container.style.height === 'auto') {
        container.style.height = `${container.offsetHeight}px`
      }
    },
    beforeStepLeave(el) {
      const container = this.$refs.massUploadContainer
      if (!container || !el) return

      container.classList.add('is-transitioning')

      const rect = el.getBoundingClientRect()
      container.style.height = `${rect.height}px`
      this.disconnectStepObserver()

      el.style.position = 'absolute'
      el.style.left = '0'
      el.style.top = '0'
      el.style.width = `${rect.width}px`
    },
    onStepEnter(el) {
      const container = this.$refs.massUploadContainer
      if (!container) return

      this.observeStepPanel(el)
      this.$nextTick(() => {
        this.setStepContainerHeight(el.offsetHeight)
      })
    },
    afterStepLeave(el) {
      if (!el) return
      el.style.position = ''
      el.style.left = ''
      el.style.top = ''
      el.style.width = ''
    },
    afterStepEnter() {
      const container = this.$refs.massUploadContainer
      if (container) container.classList.remove('is-transitioning')

      const panel = this.$el.querySelector('.mass-upload-step-panel')
      if (panel) this.setStepContainerHeight(panel.offsetHeight)
    },
    ...mapMutations({
      togglePremiumModal: 'togglePremiumModal',
    }),
    close() {
      this.$emit('close')
    },
    openFilePicker() {
      this.$refs.fileInput.click()
    },
    setDragActive(value) {
      this.isDragActive = value
    },
    handleDragLeave(event) {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return
      }

      this.setDragActive(false)
    },
    handleDrop(event) {
      this.setDragActive(false)
      this.addFiles(event.dataTransfer.files)
    },
    handleFileSelection(event) {
      this.addFiles(event.target.files)
      event.target.value = ''
    },
    addFiles(fileList) {
      this.uploadError = ''

      const incomingFiles = Array.from(fileList || [])
      if (!incomingFiles.length) {
        return
      }

      const oversizedFile = incomingFiles.find((file) => file.size > this.maxFileSizeBytes)
      if (oversizedFile) {
        this.uploadError = this.$gettext('Each file must be 5 MB or smaller.')
        return
      }

      const availableSlots = this.maxFiles - this.uploadedFiles.length
      if (availableSlots <= 0) {
        this.uploadError = this.$gettext('You can upload a maximum of 25 files.')
        return
      }

      if (incomingFiles.length > availableSlots) {
        this.uploadError = this.$gettext('You can upload a maximum of 25 files.')
      }

      this.uploadedFiles = [...this.uploadedFiles, ...incomingFiles.slice(0, availableSlots)]
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1)
      this.uploadError = ''
    },
    formatFileSize(size) {
      const sizeInMb = size / (1024 * 1024)
      return `${sizeInMb.toFixed(2)} MB`
    },
    getUploadFailureMessage(error) {
      if (error && error.status === 422) {
        return (
          error.data?.detail ||
          this.$gettext(
            'Some of the files you uploaded were not recognized as valid cost documents. Please make sure the files are in the correct format.'
          )
        )
      }

      return this.$gettext('Some files could not be uploaded. Please try again.')
    },
    async upload() {
      if (this.isUploading || !this.uploadedFiles.length) {
        return
      }

      this.isUploading = true
      this.uploadError = ''
      this.completedUploads = 0

      const filesToUpload = [...this.uploadedFiles]
      this.totalUploads = filesToUpload.length
      const failedIndexes = []
      let uploadFailureMessage = ''

      for (const [index, file] of filesToUpload.entries()) {
        try {
          const response = await AiAssistantService.scanCostImage(this.getCompanyId, file)
          if (response.data.new_supplier) {
            this.newSuppliers.push(response.data)
          }
          this.$store.commit('costs/REFRESH_COST_LIST')
        } catch (error) {
          if (error.status === 402) {
            this.$emit('close')
            this.togglePremiumModal(true)
            return
          } else if (error.status === 422) {
            uploadFailureMessage = this.getUploadFailureMessage(error)
            failedIndexes.push(index)
          } else {
            uploadFailureMessage = uploadFailureMessage || this.getUploadFailureMessage(error)
            failedIndexes.push(index)
          }
        } finally {
          this.completedUploads += 1
        }
      }

      this.isUploadingComplete = true
      setTimeout(() => {
        this.isUploading = false
        this.$emit('upload-finished')
        if (failedIndexes.length) {
          this.uploadError =
            uploadFailureMessage || this.$gettext('Some files could not be uploaded. Please try again.')
          this.uploadedFiles = filesToUpload.filter((file, index) => failedIndexes.includes(index))
          this.isUploadingComplete = false
          return
        }

        if (this.newSuppliers.length) {
          this.$emit('new-suppliers-detected', this.newSuppliers)
        }

        this.uploadedFiles = []
        this.$store.commit('costs/REFRESH_COST_LIST')
        this.$emit('close')
      }, 1500) // Add a small delay to allow users to see the completed progress bar before the modal closes
    },
  },
}
</script>

<style lang="scss" scoped>

.subtitle-text {
  font-size: 16px;
  color: $colorTextLight;
}

.mass-upload-container {
  position: relative;
  overflow: hidden;
}

.mass-upload-container.is-transitioning {
  transition: height 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.mass-upload-step-panel {
  width: 100%;
}

.mass-upload-step-enter-active,
.mass-upload-step-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.mass-upload-step-enter,
.mass-upload-step-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.mass-upload-layout {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 20rem;
  gap: 1rem;
}

.mass-upload-panel-left {
  flex: 1 1 100%;
  min-width: 0;
}

.mass-upload-panel-right {
  flex: 1 1 100%;
  min-width: 0;
}

.dropzone {
  height: 100%;
  cursor: pointer;
  border: 4px dashed rgba(55, 160, 205, 0.3);
  border-radius: 1rem;
  background: linear-gradient(180deg, #fbfdff 0%, #f7fafc 100%);
  transition: background-color 0.2s ease;
}

.dropzone:hover,
.dropzone--active {
  border-width: 4px;
  border-style: dashed;
}

.dropzone-content {
  text-align: center;
  padding: 3rem 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.dropzone-hint-icon {
  font-size: 54px;
  margin-bottom: 10px;
  color: $colorBlue;
}

.dropzone-hint-text {
  font-family: DINNextLTPro-Medium, sans-serif;
  font-size: 18px;
  color: $colorBlue;
  margin-bottom: 0.5rem;
}

.dropzone-limits {
  color: $colorUnassigned;
  font-size: 14px;
}

.uploaded-files-card {
  height: 100%;
  padding: 0.5rem;
  padding-right: 0;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #ffffff;
}

.uploaded-files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.uploaded-files-badge {
  color: #212529;
}

.gk-modal-wrapper .gk-modal .gk-modal-content .modal-content-wrap .uploaded-files-list.os-host {
  width: 100%;
  height: calc(100% - 33px);
}

.empty-uploaded-files {
  min-height: 16rem;
  padding: 1rem;
  margin-right: 0.5rem;
  border: 1px dashed #dbe4ee;
  border-radius: 0.75rem;
  background: #fafcfe;
}

.os-host,
.os-host-overflow-y {
  padding-right: 0.5rem !important;
}

.queued-file-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.queued-file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  padding-right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  transition: border-color 0.2s ease;
}

.queued-file-item:hover {
  border-color: #cbd5e1;
}

.queued-file-info {
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
}

.queued-file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: #f1f5f9;
  border-radius: 0.375rem;
  color: #64748b;
  font-size: 1rem;
  flex-shrink: 0;
  margin-right: 0.75rem;
}

.queued-file-details {
  flex-grow: 1;
  overflow: hidden;
}

.queued-file-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  margin-bottom: 0.125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queued-file-size {
  font-size: 0.75rem;
  color: #64748b;
}

.queued-file-remove-btn {
  flex-shrink: 0;
  color: #dc3545 !important;
}

.upload-error-alert {
  margin-top: 1rem;
  margin-bottom: 0;
  font-size: 14px;
}

.upload-progress {
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
}

.upload-progress__body {
  border-radius: inherit;
  padding: 1.5rem;
}

.upload-progress__stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875em;
  color: $colorUnassigned;
}

.upload-progress__success {
  display: inline-flex;
  align-items: center;
  width: auto;
  max-width: none;
  gap: 0.5rem;
}

.upload-progress__success-text {
  font-size: 0.875em;
  color: #6c757d;
  white-space: nowrap;
}

.upload-progress__success-animation {
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  overflow: hidden;
}

.upload-progress__success-animation ::v-deep > * {
  width: 100%;
  height: 100%;
}

.upload-progress__success-animation ::v-deep svg,
.upload-progress__success-animation ::v-deep canvas,
.upload-progress__success-animation ::v-deep div {
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
  max-height: 100%;
  margin: 0;
}

.upload-progress__bar {
  margin-bottom: 0.5rem;
}

.upload-progress__bar ::v-deep .progress {
  height: 0.75rem;
  border-radius: 999px;
  background: #edf2f7;
}

.upload-progress__bar ::v-deep .progress-bar {
  position: relative;
  overflow: hidden;
  background: linear-gradient(90deg, $colorBrand500 0%, $colorBrand 100%);
  transition: width 0.45s ease-in-out, background-position 0.8s ease;
  background-size: 200% 100%;
}

.upload-progress__bar--active ::v-deep .progress-bar {
  animation: upload-progress-flow 2.4s ease-in-out infinite;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 0 10px rgba($colorBrand, 0.22);
}

.upload-progress__bar--active ::v-deep .progress-bar::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0) 15%,
    rgba(255, 255, 255, 0.28) 45%,
    rgba(255, 255, 255, 0.12) 60%,
    rgba(255, 255, 255, 0) 85%
  );
  transform: translateX(-100%);
  animation: upload-progress-shade 2.1s ease-in-out infinite;
}

@keyframes upload-progress-flow {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

@keyframes upload-progress-shade {
  0% {
    transform: translateX(-115%);
    opacity: 0;
  }

  20% {
    opacity: 1;
  }

  70% {
    opacity: 1;
  }

  100% {
    transform: translateX(115%);
    opacity: 0;
  }
}

@media (min-width: 992px) {
  .mass-upload-layout {
    flex-direction: row;
  }

  .mass-upload-panel-left {
    flex-basis: 45%;
    max-width: 45%;
    transition: all 0.3s ease;
  }

  .mass-upload-panel-left.full-width {
    flex-basis: 100%;
    max-width: 100%;
  }

  .mass-upload-panel-right {
    flex-basis: 55%;
    max-width: 55%;
  }
}
</style>